'use client'

import {
    type DetailedHTMLProps,
    type HTMLAttributes,
    type ReactNode,
    useCallback,
    useEffect,
    useState,
} from 'react'
import { CSSTransition, TransitionGroup } from 'react-transition-group'

import cn from 'clsx'
import { IndicatorDots } from '@/components/indicator-dots/indicator-dots'

import cssAnimation from './CardAnimation.module.css'
import css from './QuotesCarousel.module.css'

export interface QuotesCarouselProps
    extends Omit<
        DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
        'onChange' | 'children'
    > {
    quoteWidth?: number
    quoteHeight?: number
    quoteCount?: number
    activeQuoteIndex?: number
    className?: string
    children?: (value: number) => ReactNode | undefined
    onChange?: (index: number) => void
    dotIndicatorLabels?: string[]
}

/**
 * Carousel of React elements.
 *
 * ```
 * <QuotesCarousel
 *     quoteWidth={isVertical ? 343 : 786}
 *     quoteHeight={isVertical ? 544 : 353}
 *     quoteCount={stories.length}
 * >
 *     {(index) => <ExampleStory {...stories[index]} />}
 * </QuotesCarousel>
 * ```
 */
export function QuotesCarousel({
    quoteWidth = 786,
    quoteHeight = 353,
    quoteCount = 0,
    activeQuoteIndex = 0,
    children = () => null,
    style = {},
    className,
    onChange,
    dotIndicatorLabels = [],
    ...rest
}: QuotesCarouselProps) {
    const computeNextQuoteIndex = useCallback(
        (index: number) => (index + 1 >= quoteCount ? 0 : index + 1),
        [quoteCount],
    )
    const [internalActiveQuoteIdx, setInternalActiveQuoteIdx] = useState(activeQuoteIndex)
    const [nextQuoteIdx, setNextQuoteIdx] = useState(computeNextQuoteIndex(activeQuoteIndex))

    useEffect(() => {
        setInternalActiveQuoteIdx(activeQuoteIndex)
        setNextQuoteIdx(computeNextQuoteIndex(activeQuoteIndex))
    }, [activeQuoteIndex, computeNextQuoteIndex])

    function goNext() {
        const nextInternalActiveQuoteIdx = computeNextQuoteIndex(internalActiveQuoteIdx)
        goToIndex(nextInternalActiveQuoteIdx)
    }

    function goToIndex(index: number) {
        setInternalActiveQuoteIdx(index)
        setNextQuoteIdx(
            internalActiveQuoteIdx + 2 > quoteCount - 1 ? 0 : internalActiveQuoteIdx + 2,
        )
        if (onChange) onChange(index)
    }

    const mergedStyle = {
        ...style,
        '--quote-height': `${quoteHeight}px`,
        '--quote-width': `${quoteWidth}px`,
    }

    return (
        <div className={cn(css.quotesCarousel, className)} style={mergedStyle} {...rest}>
            <div className={css.dummyCard} onClick={goNext} onKeyDown={goNext} />
            <div className={css.nextCard}>{children(nextQuoteIdx)}</div>

            <TransitionGroup component={null}>
                <CSSTransition
                    key={`tweet-${internalActiveQuoteIdx}`}
                    timeout={600}
                    classNames={cssAnimation}
                >
                    <div className={css.currentCard}>{children(internalActiveQuoteIdx)}</div>
                </CSSTransition>
            </TransitionGroup>

            <ol className={css.controls}>
                <IndicatorDots
                    labels={dotIndicatorLabels}
                    activeIndex={internalActiveQuoteIdx}
                    onSelectIndex={goToIndex}
                />
            </ol>
        </div>
    )
}
